import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Film, Mail, Lock, Eye, EyeOff, User, Sparkles } from "lucide-react";
import axios from "axios";
import { getApiBaseUrl } from "@/config/api";

const API = getApiBaseUrl();

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setAuthFromToken } = useAuth();
  
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    
    if (isSignup && !name) {
      toast.error("Please enter your name");
      return;
    }
    
    if (isSignup && password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      if (isSignup) {
        // Handle signup
        const response = await axios.post(`${API}/api/auth/signup`, {
          email,
          password,
          name
        });
        
        if (response.data.success) {
          // Auto-login after signup
          setAuthFromToken(response.data.token, response.data.user);
          
          toast.success("Welcome to anim8pro! Your free account is ready.", {
            description: "You have 5,000 free sparks to get started."
          });
          navigate("/");
        }
      } else {
        // Handle login
        const result = await login(email, password);
        if (result.success) {
          toast.success(`Welcome back, ${result.user.name || result.user.email}!`);
          
          // Redirect based on role
          if (result.user.role === "actor") {
            navigate("/actor-portal");
          } else if (result.user.role === "super_admin") {
            navigate("/super-admin");
          } else {
            navigate(from);
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSignup ? "Signup failed" : "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-3xl font-bold">
            <Film className="w-10 h-10 text-indigo-500" />
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              anim8pro
            </span>
          </div>
          <p className="text-zinc-500 mt-2">Your art. Accelerated.</p>
        </div>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-white">
              {isSignup ? "Create Free Account" : "Welcome Back"}
            </CardTitle>
            <CardDescription>
              {isSignup ? "Start creating animations in minutes" : "Sign in to your account"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignup && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                      data-testid="signup-name"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    data-testid="login-email"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSignup ? "Min 8 characters" : "••••••••"}
                    className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {!isSignup && (
                <div className="flex justify-end">
                  <Link 
                    to="/reset-password" 
                    className="text-sm text-indigo-400 hover:text-indigo-300"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isSignup ? "Creating account..." : "Signing in..."}
                  </>
                ) : (
                  isSignup ? "Create Free Account" : "Sign In"
                )}
              </Button>
              
              {isSignup && (
                <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
                  <Sparkles className="w-3 h-3 text-indigo-400" />
                  <span>Get 5,000 free sparks • No credit card required</span>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
        
        <p className="text-center text-zinc-500 text-sm mt-6">
          {isSignup ? (
            <>
              Already have an account?{" "}
              <button
                onClick={() => setIsSignup(false)}
                className="text-indigo-400 hover:text-indigo-300 font-medium"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{" "}
              <button
                onClick={() => setIsSignup(true)}
                className="text-indigo-400 hover:text-indigo-300 font-medium"
                data-testid="create-account-link"
              >
                Create free account
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
